import React from 'react';

// Path mapping to real, photorealistic pallet images
export const PALLET_IMAGE_MAP = {
  loscam: '/images/pallets/loscam.jpg',
  chep: '/images/pallets/chep.jpg',
  euro: '/images/pallets/epal.jpg',
  wooden: '/images/pallets/wooden.jpg',
  plastic: '/images/pallets/plastic.jpg',
  open_base: '/images/pallets/open_base.jpg',
  closed_base: '/images/pallets/closed_base.jpg'
};

/**
 * Authentic Real Photo Graphic for Open Base Pallet (พาเลทเปิด)
 * Shows real timber planks, spacer blocks, and open bottom runners.
 */
export function OpenBasePalletSVG({ isSelected = false, className = '' }) {
  return (
    <div className={`relative w-full rounded-xl overflow-hidden bg-white flex flex-col items-center justify-center border transition-all ${
      isSelected ? 'border-emerald-500 shadow-sm' : 'border-slate-200'
    } ${className}`}>
      <div className="relative w-full h-24 overflow-hidden bg-gradient-to-b from-white to-slate-50 flex items-center justify-center p-1.5">
        <img 
          src={PALLET_IMAGE_MAP.open_base} 
          alt="Open Base Pallet" 
          className="w-full h-full object-contain drop-shadow-md transition-transform duration-200 hover:scale-105"
          loading="lazy"
        />
        {/* Floating Open Tag */}
        <div className="absolute top-1.5 right-1.5 bg-emerald-600 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-200 animate-ping" />
          <span>พื้นเปิดโล่ง (Open)</span>
        </div>
      </div>
      <div className="w-full px-2 py-1 bg-emerald-50/70 border-t border-emerald-100 flex items-center justify-between text-[10px] text-emerald-800 font-semibold">
        <span>ล้อ Hand Pallet สัมผัสพื้นได้</span>
        <span>✓ 2-Way / 4-Way</span>
      </div>
    </div>
  );
}

/**
 * Authentic Real Photo Graphic for Closed Base Pallet (พาเลทปิด / Perimeter Base / CHEP)
 * Shows bottom perimeter wooden boards enclosing all 4 sides.
 */
export function ClosedBasePalletSVG({ isSelected = false, className = '' }) {
  return (
    <div className={`relative w-full rounded-xl overflow-hidden bg-white flex flex-col items-center justify-center border transition-all ${
      isSelected ? 'border-blue-500 shadow-sm' : 'border-slate-200'
    } ${className}`}>
      <div className="relative w-full h-24 overflow-hidden bg-gradient-to-b from-white to-slate-50 flex items-center justify-center p-1.5">
        <img 
          src={PALLET_IMAGE_MAP.closed_base} 
          alt="Closed Perimeter Base Pallet" 
          className="w-full h-full object-contain drop-shadow-md transition-transform duration-200 hover:scale-105"
          loading="lazy"
        />
        {/* Floating Closed Tag */}
        <div className="absolute top-1.5 right-1.5 bg-blue-600 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-200" />
          <span>ฐานปิดรอบ (Perimeter)</span>
        </div>
      </div>
      <div className="w-full px-2 py-1 bg-blue-50/70 border-t border-blue-100 flex items-center justify-between text-[10px] text-blue-800 font-semibold">
        <span>ไม้คานล่างปิดรอบ 4 ด้าน</span>
        <span>⚠️ รถขาคร่อม/Forklift</span>
      </div>
    </div>
  );
}

/**
 * Authentic Real Photo Thumbnail for Pallet Presets (Loscam, CHEP, EPAL, Plastic, Wood)
 */
export function PalletPresetThumbnail({ presetId, isSelected = false, className = '' }) {
  let imgPath = PALLET_IMAGE_MAP.loscam;
  if (presetId === 'chep') imgPath = PALLET_IMAGE_MAP.chep;
  else if (presetId === 'euro') imgPath = PALLET_IMAGE_MAP.euro;
  else if (presetId === 'wooden') imgPath = PALLET_IMAGE_MAP.wooden;
  else if (presetId === 'plastic') imgPath = PALLET_IMAGE_MAP.plastic;

  return (
    <div className={`relative w-full h-full flex items-center justify-center overflow-hidden rounded-lg ${className}`}>
      <img 
        src={imgPath} 
        alt={presetId} 
        className="w-full h-full object-contain drop-shadow-xs transition-transform duration-200 group-hover:scale-105"
        loading="lazy"
      />
    </div>
  );
}

/**
 * Authentic Realistic Pallet & Cargo Visualizer Card
 * Displays real photographic imagery of the selected pallet type, with live dimension callouts,
 * cargo height, overhang markers, and warehouse parameters.
 */
export function PalletLoadVisualizer({ pallet }) {
  if (!pallet) return null;

  const width = pallet.width || 1000;
  const length = pallet.length || 1200;
  const totalHeight = pallet.height || 1500;
  const baseHeight = pallet.baseHeight || 150;
  const overhang = pallet.overhang || 0;
  const maxWeight = pallet.maxWeight || 1000;
  const brand = pallet.brand || 'loscam';
  const entryType = pallet.entryType || 'open';

  // Choose photographic asset based on brand
  let palletPhoto = PALLET_IMAGE_MAP.loscam;
  let brandName = 'Loscam Pallet (ไม้แดง)';
  let brandBadgeBg = 'bg-red-500/20 text-red-300 border-red-500/30';

  if (brand === 'chep') {
    palletPhoto = PALLET_IMAGE_MAP.chep;
    brandName = 'CHEP Pallet (ไม้น้ำเงิน)';
    brandBadgeBg = 'bg-blue-500/20 text-blue-300 border-blue-500/30';
  } else if (brand === 'plastic') {
    palletPhoto = PALLET_IMAGE_MAP.plastic;
    brandName = 'Plastic Cleanroom (พลาสติก)';
    brandBadgeBg = 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30';
  } else if (brand === 'wooden') {
    palletPhoto = PALLET_IMAGE_MAP.wooden;
    brandName = 'พาเลทไม้ทั่วไป (Open Wood)';
    brandBadgeBg = 'bg-amber-500/20 text-amber-300 border-amber-500/30';
  } else if (brand === 'custom' || brand === 'euro') {
    palletPhoto = PALLET_IMAGE_MAP.euro;
    brandName = 'Euro Pallet (EPAL 800×1200)';
    brandBadgeBg = 'bg-purple-500/20 text-purple-300 border-purple-500/30';
  }

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-850 to-slate-950 p-3.5 rounded-2xl border border-slate-700/80 shadow-lg text-white space-y-3">
      {/* Header Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span 
            className="w-2.5 h-2.5 rounded-full ring-2 ring-white/30 shrink-0" 
            style={{ backgroundColor: pallet.color || '#ef4444' }} 
          />
          <span className="font-extrabold text-xs tracking-tight text-white truncate max-w-[180px]">
            {pallet.name || brandName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${brandBadgeBg}`}>
            {brand.toUpperCase()}
          </span>
          {entryType === 'open' ? (
            <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full font-bold">
              ✓ Open
            </span>
          ) : (
            <span className="text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full font-bold">
              ▦ Closed
            </span>
          )}
        </div>
      </div>

      {/* Real Pallet Photo Showcase with Cargo & Engineering Dimensions */}
      <div className="relative w-full rounded-xl bg-gradient-to-b from-slate-800/80 to-slate-900/90 border border-slate-700/60 p-3 overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-[radial-gradient(#475569_1px,transparent_1px)] [background-size:16px_16px] opacity-25" />

        {/* Pallet Photo Display */}
        <div className="relative z-10 w-full h-36 flex items-center justify-center">
          <img 
            src={palletPhoto} 
            alt={brandName} 
            className="w-full h-full object-contain drop-shadow-[0_12px_16px_rgba(0,0,0,0.6)] transition-transform duration-300 hover:scale-105"
            loading="lazy"
          />

          {/* Overhang Alert Badge if > 0 */}
          {overhang > 0 && (
            <div className="absolute top-2 left-2 bg-red-600/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-md border border-red-400 shadow-md flex items-center gap-1">
              <span>⚠️ Overhang: +{overhang} mm</span>
            </div>
          )}

          {/* Dimension Overlays */}
          <div className="absolute bottom-1 left-2 bg-slate-950/80 backdrop-blur-xs text-[10px] font-bold text-emerald-300 px-2 py-0.5 rounded border border-emerald-500/40 shadow-xs">
            W: {width} mm
          </div>
          <div className="absolute bottom-1 right-2 bg-slate-950/80 backdrop-blur-xs text-[10px] font-bold text-sky-300 px-2 py-0.5 rounded border border-sky-500/40 shadow-xs">
            L: {length} mm
          </div>
          <div className="absolute top-2 right-2 bg-slate-950/80 backdrop-blur-xs text-[10px] font-bold text-amber-300 px-2 py-0.5 rounded border border-amber-500/40 shadow-xs">
            สูงรวม: {(totalHeight / 1000).toFixed(2)} ม.
          </div>
        </div>
      </div>

      {/* Footer KPI bar */}
      <div className="grid grid-cols-3 gap-2 pt-1 border-t border-slate-800 text-center text-[10px]">
        <div className="bg-slate-800/80 p-2 rounded-xl border border-slate-700/50">
          <div className="text-slate-400 text-[9px] uppercase font-semibold">ขนาดมิติ (W×L)</div>
          <div className="font-extrabold text-slate-100 text-xs mt-0.5">{width}×{length} mm</div>
        </div>
        <div className="bg-slate-800/80 p-2 rounded-xl border border-slate-700/50">
          <div className="text-slate-400 text-[9px] uppercase font-semibold">สูงรวมสินค้า (H)</div>
          <div className="font-extrabold text-emerald-400 text-xs mt-0.5">{(totalHeight / 1000).toFixed(2)} ม.</div>
        </div>
        <div className="bg-slate-800/80 p-2 rounded-xl border border-slate-700/50">
          <div className="text-slate-400 text-[9px] uppercase font-semibold">น้ำหนักสินค้ารวม</div>
          <div className="font-extrabold text-amber-400 text-xs mt-0.5">{maxWeight.toLocaleString()} kg</div>
        </div>
      </div>
    </div>
  );
}
